import { useEffect, useState } from 'react';
import { AlertModal } from '../../../confirmModal';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    Exercise,
    LabelBody,
    LabelBodyHeadline,
    LabelDisplay1,
    LabelDisplay2,
    MultipleChoiceButton,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import {
    IllustrationBulb,
    IllustrationPenPaper,
    Module3S1Exercise1Illustration1,
    Module3S1Exercise1Illustration2,
    Module3S1Exercise1Illustration4,
} from '../../icons';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import {
    normalizeInput,
    parseArray,
    useSyncedState,
} from '../../useSyncedState';
import { VideoExerciseView } from '../../views';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page2And3MultipleChoicesKey = 'exercise311Page2And3MultipleChoicesKey';
const page4TextInputKey = 'exercise311Page4TextInputKey';
const page7Text1Key = 'exercise311Page7Text1Key';
const page7Text2Key = 'exercise311Page7Text2Key';

const dataKeys: string[] = [
    page2And3MultipleChoicesKey,
    page4TextInputKey,
    page7Text1Key,
    page7Text2Key,
];

export const exerciseData311: ExerciseData = {
    ExerciseView: Module3S1Exercise1,
    dataKeys,
    questions: [
        createQuestionFromConfiguration({
            title: 'Ekstra refleksion',
            description:
                'Var der noget af det, du har tænkt over, der kom bag på dig eller har sat gang i nye tanker?',
            dataKeys: [page4TextInputKey],
        }),
        createQuestionFromConfiguration({
            title: 'Hvordan var det at udfylde de to cirkler?',
            description:
                'Overvej, hvorfor din idealcirkel ser ud, som den gør. Hvad viser den dig?',
            dataKeys: [page7Text1Key],
        }),
        createQuestionFromConfiguration({
            title: 'Vælg én lille handling, du kan gøre allerede nu, for at komme nærmere din ideelle cirkel',
            description:
                'Det er ikke muligt at ændre alt på én gang. Det vigtige er at vælge et sted, hvor du vil starte.',
            dataKeys: [page7Text2Key],
        }),
    ],
};

type MultipleChoiceOption = {
    id: number;
    text: string;
};

const multipleChoiceOptions: MultipleChoiceOption[] = [
    {
        id: 0,
        text: 'Hvem i din hverdag inspirerer dig, og hvad kan du lære af dem?',
    },
    {
        id: 1,
        text: 'Hvad værdsætter du mest ved dig selv, og hvordan ønsker du at dele det med andre?',
    },
    {
        id: 2,
        text: 'Hvis du kunne gøre en forskel i én persons liv i dag, hvad ville du så gøre for dem?',
    },
    {
        id: 3,
        text: 'Hvad drømmer du om at skabe eller bidrage til i verden, og hvorfor betyder den drøm noget for dig?',
    },
];

export function Module3S1Exercise1({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Dit kompas">
            <ProgressBar current={exercise.CurrentPage} total={8} />
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            case 1:
                return (
                    <Page1
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        page2MultipleChoices={parseArray(
                            exercise.Data[page2And3MultipleChoicesKey],
                        )}
                        onContinue={(multipleChoices) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                                data: {
                                    [page2And3MultipleChoicesKey]:
                                        multipleChoices,
                                },
                            });
                        }}
                    />
                );
            case 3:
                return (
                    <Page3
                        page3MultipleChoices={parseArray(
                            exercise.Data[page2And3MultipleChoicesKey],
                        )}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 4,
                            });
                        }}
                    />
                );
            case 4:
                return (
                    <Page4
                        textInput={exercise.Data[page4TextInputKey] ?? ''}
                        onContinue={(textInput) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 5,
                                data: {
                                    [page4TextInputKey]:
                                        normalizeInput(textInput),
                                },
                            });
                        }}
                    />
                );
            case 5:
                return (
                    <Page5
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 6,
                            });
                        }}
                    />
                );
            case 6:
                return (
                    <Page6
                        exercise={exercise}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 7,
                            });
                        }}
                    />
                );
            case 7:
                return (
                    <Page7
                        savedData={{
                            text1: exercise.Data[page7Text1Key] ?? '',
                            text2: exercise.Data[page7Text2Key] ?? '',
                        }}
                        onContinue={(data) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 8,
                                data: {
                                    [page7Text1Key]: normalizeInput(data.text1),
                                    [page7Text2Key]: normalizeInput(data.text2),
                                },
                            });
                        }}
                    />
                );
            case 8:
                return (
                    <Page8
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                    />
                );
        }
    }
}

type PageProps = {
    onContinue: () => void;
};

function Page1({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">
                Se nærmere på dit kompas
            </LabelDisplay2>
            <LabelBody>
                Kompasset kan hjælpe dig med at få reflekteret over, hvad der
                betyder noget for dig, og om du har plads nok i din hverdag til
                det. Der er først nogle refleksionsspørgsmål til dig og derefter
                en video, der guider dig i at lave dit eget kompas. Spørgsmålene
                kan hjælpe dig i gang med en proces, hvor du kommer nærmere dine
                egne personlige værdier og interesser.
            </LabelBody>
            <Module3S1Exercise1Illustration1 className="mx-auto mt-30" />
            <SwirlyBox class="my-30">Er du klar til at gå i gang?</SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Start øvelsen
            </SecondaryButton>
        </>
    );
}

type Page2Props = {
    page2MultipleChoices: string[];
    onContinue: (multipleChoices: string) => void;
};

function Page2({ page2MultipleChoices, onContinue }: Page2Props) {
    const [_multipleChoices, setMultipleChoices] =
        useSyncedState(page2MultipleChoices);
    const [showModal, setShowModal] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    useEffect(() => {
        if (_multipleChoices.length !== 0) setShowErrorMessage(false);
    }, [_multipleChoices]);

    return (
        <>
            {showModal && (
                <AlertModal
                    title="Du har valgt spørgsmål nok"
                    body="I denne øvelse er det nok at tænke over to spørgsmål ad gangen. Hvis du har mod på det, kan du altid komme tilbage senere og lave øvelsen igen med nye spørgsmål."
                    onClose={() => setShowModal(false)}
                    text="OK"
                    class="p-30"
                />
            )}
            <LabelDisplay2 class="mb-10">Først en refleksion</LabelDisplay2>
            <LabelBody class="mb-30">
                Vælg to af de spørgsmål, som du får lyst til at svare på. Hvis
                det er interessant for dig, kan du altid vende tilbage og
                forsøge at svare på resten.
            </LabelBody>
            {multipleChoiceOptions.map((m) => (
                <MultipleChoiceButton
                    key={m.id}
                    class="mb-20"
                    text={m.text}
                    isClicked={_multipleChoices.includes(String(m.id))}
                    onClickHandler={(addToList: boolean) => {
                        if (addToList) {
                            if (!_multipleChoices.includes(String(m.id))) {
                                setMultipleChoices([
                                    ..._multipleChoices,
                                    String(m.id),
                                ]);
                            }
                        } else {
                            setMultipleChoices(
                                _multipleChoices.filter(
                                    (choice) => choice !== String(m.id),
                                ),
                            );
                        }
                    }}
                ></MultipleChoiceButton>
            ))}
            {showErrorMessage && (
                <div className="text-sm text-falck-red">
                    Du skal vælge minimum et spørgsmål for at fortsætte.
                </div>
            )}
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    if (_multipleChoices.length > 2) {
                        setShowModal(true);
                    } else if (_multipleChoices.length === 0) {
                        setShowErrorMessage(true);
                    } else {
                        onContinue(JSON.stringify(_multipleChoices));
                    }
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    page3MultipleChoices: string[];
    onContinue: () => void;
};

function Page3({ page3MultipleChoices, onContinue }: Page3Props) {
    return (
        <>
            <LabelDisplay1 class="mb-10">Tænk over dit svar</LabelDisplay1>
            <LabelBody class="mb-30">
                Hvad vil du svare på de spørgsmål du har valgt?
            </LabelBody>
            <LabelBodyHeadline color="text-dark-red">
                Dine spørgsmål:
            </LabelBodyHeadline>
            <LabelBody className="mt-10">
                <BulletList
                    items={multipleChoiceOptions
                        .filter((option) =>
                            page3MultipleChoices.includes(String(option.id)),
                        )
                        .map((option) => option.text)}
                />
            </LabelBody>
            <Module3S1Exercise1Illustration2 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page4Props = {
    textInput: string;
    onContinue: (text1: string) => void;
};

function Page4({ textInput, onContinue }: Page4Props) {
    const [text, setText] = useSyncedState(textInput);

    return (
        <>
            <Card class="mb-30">
                <IllustrationPenPaper />
                <LabelDisplay1 class="mb-10 mt-20">
                    Ekstra refleksion
                </LabelDisplay1>
                <LabelBody>
                    Var der noget af det, du har tænkt over, der kom bag på dig
                    eller har sat gang i nye tanker?
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody class="mt-10">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue(text);
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page5({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Klar til at sætte kurs</LabelDisplay2>
            <LabelBody>
                Vi håber, at de foregående spørgsmål har sat dine tanker i gang
                med, hvad du holder af i verden. Det skal hjælpe dig til at
                tegne dit eget kompas og se på, hvilken kurs det har - og om der
                er noget ved den kurs, du vil ændre.
            </LabelBody>
            <Module3S1Exercise1Illustration4 className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page6Props = {
    exercise: ExerciseDto;
    onContinue: () => void;
};

function Page6({ exercise, onContinue }: Page6Props) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Tegn dit kompas</LabelDisplay2>
            <LabelBody>
                Videoen vil guide dig i, hvordan øvelsen laves. Du skal bruge
                papir og blyant. Formålet er at få dig til at tegne det kompas,
                der er relevant for dig, ud fra hvad der er vigtigt for dig at
                bruge tid og energi på.
            </LabelBody>
            <VideoExerciseView
                className="mt-30"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+09+-+V05+-+Online+-+241220+-+JK.00_01_00_03.Still008.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+09+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:03:17',
                    Width: 1920,
                    Height: 1080,
                }}
            />
            <SwirlyBox class="my-30">
                Når du har tegnet dit kompas, kan du gå videre
            </SwirlyBox>
            <Spacer />
            <SecondaryButton onClick={onContinue}>Fortsæt</SecondaryButton>
        </>
    );
}

type Page7Data = { text1: string; text2: string };
type Page7Props = {
    savedData: Page7Data;
    onContinue: (data: Page7Data) => void;
};

function Page7({ savedData, onContinue }: Page7Props) {
    const [text1, setText1] = useSyncedState(savedData.text1);
    const [text2, setText2] = useSyncedState(savedData.text2);

    return (
        <>
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Hvordan var det at udfylde de to cirkler?
                </LabelDisplay1>
                <LabelBody>
                    Overvej, hvorfor din idealcirkel ser ud, som den gør. Hvad
                    viser den dig?
                </LabelBody>
                <Textarea
                    className="mb-10 mt-20"
                    text={text1}
                    onChangeText={setText1}
                />
                <LabelDisplay1 class="mb-10">
                    Vælg én lille handling, du kan gøre allerede nu, for at
                    komme nærmere din ideelle cirkel
                </LabelDisplay1>
                <LabelBody>
                    Det er ikke muligt at ændre alt på én gang. Det vigtige er
                    at vælge et sted, hvor du vil starte.
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={text2}
                    onChangeText={setText2}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton
                onClick={() => {
                    onContinue({ text1, text2 });
                }}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

function Page8({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Hvad der er vigtigt for dig, kan ændre sig over tid. Kompasset
                er et værktøj, der er godt at hive frem flere gange igennem
                livet f.eks. en gang om året. På den måde giver du dig selv
                muligheden for at tjekke ind med, om du fortsat har en hverdag,
                der indeholder det, du gerne vil have, den skal indeholde.
            </LabelBody>
            <IllustrationBulb className="mx-auto my-30" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
